// 15D — Report Template & Layout Center (SuperAdmin): template visibili,
// modificabili, versionabili e testabili con documenti di prova reali
// PDF/DOCX/XLSX (pipeline di rendering vera, dicitura BOZZA).
import { useCallback, useEffect, useState } from "react";
import { FileText, Plus, Download, CopyPlus, Pencil, X, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_STYLE = {
  ACTIVE: "bg-green-50 text-green-800 border-green-300",
  DRAFT: "bg-amber-50 text-amber-800 border-amber-300",
  DEPRECATED: "bg-slate-100 text-slate-500 border-slate-300",
};
const SCOPE_PRESETS = {
  VSME_BASE: { type: "VSME", modules: ["BASIC"] },
  VSME_FULL: { type: "VSME", modules: ["BASIC", "COMPREHENSIVE"] },
  MEF_DOSSIER: { type: "MEF" },
  SCORE: { type: "SCORE" },
};
const EMPTY_NEW = { code: "", title: "", description: "", scope_preset: "VSME_BASE" };
const FORMATS = ["pdf", "docx", "xlsx"];

export default function TemplateCenter() {
  const [templates, setTemplates] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newTpl, setNewTpl] = useState(EMPTY_NEW);
  const [downloading, setDownloading] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/report-templates");
      setTemplates(data.templates || []);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/report-templates/${editing.id}`, {
        title: editing.title, description: editing.description,
        include_narrative: editing.include_narrative, status: editing.status,
      });
      toast.success("Template aggiornato",
        { description: "Le modifiche si applicano ai documenti generati da ora in poi." });
      setEditing(null);
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const createTemplate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/report-templates", {
        code: newTpl.code, title: newTpl.title, description: newTpl.description,
        audience: "BANCA", scope: SCOPE_PRESETS[newTpl.scope_preset],
        include_narrative: true,
      });
      toast.success("Template creato (v1, attivo)");
      setCreating(false);
      setNewTpl(EMPTY_NEW);
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const newVersion = async (tpl) => {
    try {
      const { data } = await api.post(`/admin/report-templates/${tpl.id}/new-version`);
      toast.success(`Nuova versione ${data.version} creata in bozza`,
        { description: "Le istanze già generate restano legate alla versione originale." });
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Eliminare definitivamente il template "${tpl.title}" (${tpl.code} ${tpl.version})? L'operazione è bloccata se esistono report già generati con questo template.`)) return;
    try {
      await api.delete(`/admin/report-templates/${tpl.id}`);
      toast.success("Template eliminato");
      await load();
    } catch (e) {
      toast.error("Eliminazione non possibile", { description: formatApiError(e) });
    }
  };

  const testDocument = async (tpl, format) => {
    setDownloading(`${tpl.id}-${format}`);
    try {
      const { data, headers } = await api.get(
        `/admin/report-templates/${tpl.id}/test-document`,
        { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prova_${tpl.code.toLowerCase()}_${tpl.version}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Documento di prova ${format.toUpperCase()} scaricato`,
        { description: `${(data.size / 1024).toFixed(0)} KB · dicitura BOZZA` });
    } catch (e) {
      toast.error("Generazione fallita", { description: formatApiError(e) });
    } finally {
      setDownloading("");
    }
  };

  if (error) return <p className="text-sm text-red-700" data-testid="tpl-error">{error}</p>;
  if (!templates) return <p className="text-sm text-slate-600" data-testid="tpl-loading">Caricamento…</p>;

  return (
    <div className="space-y-6" data-testid="template-center-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-green-800" aria-hidden="true" />
            Report Template &amp; Layout Center
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Modelli reali per PDF, Word ed Excel: anteprima di prova, versionamento e layout.
            I documenti di prova sono generati dalla pipeline reale con dicitura BOZZA.
          </p>
        </div>
        <Button className="bg-green-700 hover:bg-green-800" onClick={() => setCreating((v) => !v)}
          data-testid="tpl-create-toggle">
          <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Nuovo template
        </Button>
      </div>

      {creating && (
        <Card className="border-slate-200" data-testid="tpl-create-card">
          <CardHeader className="pb-2"><CardTitle className="text-base">Nuovo template</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={createTemplate} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="tpl-code">Codice *</Label>
                <Input id="tpl-code" required minLength={3} value={newTpl.code}
                  placeholder="ES. REPORT_SOSTENIBILITA_2026"
                  onChange={(e) => setNewTpl((f) => ({ ...f, code: e.target.value }))}
                  data-testid="tpl-code-input" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="tpl-title">Titolo *</Label>
                <Input id="tpl-title" required value={newTpl.title}
                  onChange={(e) => setNewTpl((f) => ({ ...f, title: e.target.value }))}
                  data-testid="tpl-title-input" />
              </div>
              <div className="space-y-1">
                <Label>Contenuto (scope)</Label>
                <Select value={newTpl.scope_preset}
                  onValueChange={(v) => setNewTpl((f) => ({ ...f, scope_preset: v }))}>
                  <SelectTrigger data-testid="tpl-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VSME_BASE">VSME — Modulo Base</SelectItem>
                    <SelectItem value="VSME_FULL">VSME — Base + Comprehensive</SelectItem>
                    <SelectItem value="MEF_DOSSIER">Dossier MEF PMI-Banche</SelectItem>
                    <SelectItem value="SCORE">ESG PMI Score</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label htmlFor="tpl-desc">Descrizione</Label>
                <Input id="tpl-desc" value={newTpl.description}
                  onChange={(e) => setNewTpl((f) => ({ ...f, description: e.target.value }))}
                  data-testid="tpl-desc-input" />
              </div>
              <div className="flex gap-2 md:col-span-2">
                <Button type="submit" className="bg-green-700 hover:bg-green-800"
                  data-testid="tpl-create-submit">Crea template</Button>
                <Button type="button" variant="outline" onClick={() => setCreating(false)}
                  data-testid="tpl-create-cancel">
                  <X className="w-4 h-4 mr-1" aria-hidden="true" /> Annulla</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200" data-testid="tpl-list-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">Template registrati ({templates.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {templates.map((tpl) => (
            <div key={tpl.id} className="rounded-lg border border-slate-200 p-3 space-y-2"
              data-testid={`tpl-row-${tpl.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs text-slate-700">{tpl.code}</span>
                <Badge variant="outline" className="text-[10px]">{tpl.version}</Badge>
                <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[tpl.status] || ""}`}
                  data-testid={`tpl-status-${tpl.id}`}>{tpl.status}</Badge>
                <span className="text-sm font-medium text-slate-900 flex-1">{tpl.title}</span>
                <Button size="sm" variant="ghost" onClick={() => setEditing({ ...tpl })}
                  data-testid={`tpl-edit-${tpl.id}`} aria-label="Modifica">
                  <Pencil className="w-3.5 h-3.5" aria-hidden="true" /></Button>
                <Button size="sm" variant="outline" onClick={() => newVersion(tpl)}
                  data-testid={`tpl-newver-${tpl.id}`}>
                  <CopyPlus className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Nuova versione
                </Button>
                <Button size="sm" variant="ghost" className="text-red-700 hover:bg-red-50" onClick={() => deleteTemplate(tpl)}
                  data-testid={`tpl-delete-${tpl.id}`} aria-label="Elimina template">
                  <Trash2 className="w-3.5 h-3.5" aria-hidden="true" /></Button>
                <div className="flex gap-1">
                  {FORMATS.map((f) => (
                    <Button key={f} size="sm" variant="outline"
                      disabled={downloading === `${tpl.id}-${f}`}
                      onClick={() => testDocument(tpl, f)}
                      data-testid={`tpl-test-${f}-${tpl.id}`}>
                      <Download className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                      {downloading === `${tpl.id}-${f}` ? "…" : f.toUpperCase()}
                    </Button>
                  ))}
                </div>
              </div>
              {tpl.description && <p className="text-xs text-slate-500">{tpl.description}</p>}
            </div>
          ))}
        </CardContent>
      </Card>

      {editing && (
        <Card className="border-green-200 bg-green-50/40" data-testid="tpl-edit-card">
          <CardHeader className="pb-2"><CardTitle className="text-base">
            Modifica {editing.code} {editing.version}
          </CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={saveEdit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="edit-title">Titolo</Label>
                <Input id="edit-title" value={editing.title}
                  onChange={(e) => setEditing((f) => ({ ...f, title: e.target.value }))}
                  data-testid="tpl-edit-title" />
              </div>
              <div className="space-y-1">
                <Label>Stato</Label>
                <Select value={editing.status}
                  onValueChange={(v) => setEditing((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="tpl-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(STATUS_STYLE).map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label htmlFor="edit-desc">Descrizione</Label>
                <Input id="edit-desc" value={editing.description || ""}
                  onChange={(e) => setEditing((f) => ({ ...f, description: e.target.value }))}
                  data-testid="tpl-edit-desc" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 md:col-span-2">
                <input type="checkbox" checked={!!editing.include_narrative}
                  onChange={(e) => setEditing((f) => ({ ...f, include_narrative: e.target.checked }))}
                  data-testid="tpl-edit-narrative" />
                Includi narrazione AI per sezione
              </label>
              <div className="flex gap-2 md:col-span-2">
                <Button type="submit" className="bg-green-700 hover:bg-green-800"
                  data-testid="tpl-edit-save">Salva</Button>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}
                  data-testid="tpl-edit-cancel">Annulla</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
